import { useEffect, useState } from 'react';
import type { GraphIndex } from '../data/types';

/**
 * Resolve a path relative to the app's base URL. Handles both root and
 * sub-path deployments (GitHub Pages), and honours Vite's ``base`` config.
 */
export function dataUrl(path: string): string {
  const base = import.meta.env.BASE_URL || '/';
  const trimmed = path.replace(/^\/+/, '');
  return `${base}${base.endsWith('/') ? '' : '/'}${trimmed}`;
}

// Singleton: graph.json is small (~1 MB) and referenced by every screen;
// we fetch it exactly once per page load, share the promise across
// consumers, and never re-fetch.
let cache: GraphIndex | null = null;
let pending: Promise<GraphIndex> | null = null;

async function fetchGraph(): Promise<GraphIndex> {
  if (cache) return cache;
  if (pending) return pending;
  pending = fetch(dataUrl('data/graph.json'))
    .then((r) => {
      if (!r.ok) throw new Error(`graph.json ${r.status}`);
      return r.json() as Promise<GraphIndex>;
    })
    .then((g) => {
      cache = g;
      pending = null;
      return g;
    })
    .catch((err) => {
      pending = null;
      throw err;
    });
  return pending;
}

export interface GraphIndexState {
  data: GraphIndex | null;
  error: Error | null;
  loading: boolean;
}

export function useGraphIndex(): GraphIndexState {
  const [state, setState] = useState<GraphIndexState>(() => ({
    data: cache,
    error: null,
    loading: cache === null,
  }));

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    fetchGraph()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: null, error, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
